import { observable, runInAction, action, makeObservable, computed } from "mobx";
import { BrowserStorage } from "utils/BrowserStorage";
import { API_URL } from "General/APIEndpoints";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { userInfoStore } from "Stores/UserInfoStore";
import { infoBoxSchema } from "Stores/helpers";
import { titleStore } from "Stores/TitleStore";

const dataStore = {
  preferredLogin: "Login-Preferred",
  SAVE_USERNAME: "SAVE_USERNAME",
};

/**
 * These Default Settings are used as a fallback if the Settings could not be retrieved.
 */
const emptyData: API_LoginSettings = {
  highSecurity: true,
  availableLogins: ["Default"],
  siteTitle: "PMG",
  passwordDefinitions: {
    complexityClass: 1,
    expiresInDays: 0,
    historyCount: 0,
    minimumLength: 10,
    selfResetAllowed: false,
    trivialPasswordsAllowed: false,
  },
  backendVersion: "",
  theme: "DEFAULT",
};

export class LoginSettingsStore extends CoreStore {
  _currentLoginMethod: API_LOGINSETTINGS_AVAILABLELOGINS = BrowserStorage.store(dataStore.preferredLogin) || "Default";
  data: API_LoginSettings = Object.assign({}, emptyData);
  passwordPolicy: API_PasswordPolicy = null;
  isValid: boolean = false; // valid flag from checkPasswordComplexity for button control in LoginChangePass

  constructor() {
    super({ omitAllStoresArray: true, id: "id" });

    makeObservable(this, {
      _currentLoginMethod: observable,
      data: observable,
      passwordPolicy: observable,
      isValid: observable,
      clear: action.bound,
      passwordDefinitions: action.bound,
      setIsValid: action.bound,
      fetch: action.bound,
      setTheme: action.bound,
      clearPasswordPolicy: action.bound,
      setPasswordDefinitions: action.bound,
      theme: computed,
    });
  }

  clear() {
    this.data = Object.assign({}, emptyData, { theme: this.data?.theme });
    this.passwordPolicy = null;
  }

  url() {
    return API_URL.LOGIN.getSettings();
  }

  setTheme(theme: API_LOGINSETTINGS_THEME) {
    this.data.theme = theme;
  }

  @handleStoreState()
  async fetch() {
    try {
      // Don't use any additional headers, as we don't want to authenticate the request.
      const response: API_LoginSettings = await API.getData(this.url(), null, { withoutAuth: true });
      runInAction(() => {
        this.data = response;
        if (this.data.dataRoomOnly) {
          infoBoxSchema.find((i) => i.backendModule === "DOCUMENTS_AND_PLANS").label = "Documents";
        }
        // console.log("response.siteTitle", response.siteTitle);
        titleStore.setDefaultTitle(response.siteTitle);
      });
    } catch (error) {
      console.warn("Failed to load login settings, setting DEFAULT theme");
      console.log("Caused by error:", error, error.message);
      throw error;
    } finally {
      window.setTimeout(function () {
        document.getElementById("loading-drawer").style.display = "none";
        window.Loader.stop();
      }, 1000);
      document.body.classList.add("loaded");
    }
  }

  get documentsAndPlansTitle() {
    return this.data.dataRoomOnly ? "Documents" : "Documents & plans";
  }

  async passwordDefinitions(resetKey?: string | number) {
    try {
      const param = resetKey ? resetKey : userInfoStore.id;
      const response: API_PasswordPolicy = await API.getData(API_URL.PASSWORD.getPasswordPolicyForUserByKey(param));
      runInAction(() => {
        if (response) {
          this.passwordPolicy = response;
        }
      });
    } catch (err) {
      throw err;
    }
  }

  async setPasswordDefinitions(response: API_PasswordPolicy) {
    this.passwordPolicy = response;
  }

  async setIsValid(flag: boolean) {
    this.isValid = flag;
  }

  clearPasswordPolicy() {
    this.passwordPolicy = null;
  }

  set currentLoginMethod(method: API_LOGINSETTINGS_AVAILABLELOGINS) {
    BrowserStorage.store(dataStore.preferredLogin, method);
    runInAction(() => {
      this._currentLoginMethod = method;
    });
  }
  get currentLoginMethod() {
    return this._currentLoginMethod;
  }

  get theme(): API_LOGINSETTINGS_THEME {
    return this.data?.theme || "DEFAULT";
  }
}

export const loginSettingsData = new LoginSettingsStore();
